// Remembers how a visitor reached the site, so the quote form can say so.
//
// Google adds a click id (?gclid=…) to every ad click. It is gone from the
// address bar the moment the visitor clicks to another page, and many people
// take a day, a week, or a phone call to a colleague before they send the
// form. So the click is remembered here, in this browser only, for the same 90
// days Google itself credits an ad click. The quote forms send it with the
// enquiry and api/sendmail.js files it on the lead — that is what lets the
// admin trace an invoice back to the ad that started it.
//
// captureVisit() runs ONCE per page load from src/index.js, before React
// renders, so it never touches what the pre-rendered landing pages draw.
// Everything here fails silently: no storage (private browsing, blocked
// cookies) just means the enquiry arrives without the extra detail.

const KEY = "a1_visit_v1";
const WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
const CLICK_IDS = ["gclid", "gbraid", "wbraid"];
const UTM = ["source", "medium", "campaign", "term", "content"];

// The same state kept in memory, for browsers where storage is unavailable:
// the visit that is still open in this tab can at least be reported.
let memo = null;

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") return parsed;
  } catch {
    /* storage blocked or corrupt — fall through to memory */
  }
  return memo || {};
}

function write(state) {
  memo = state;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* memory copy is enough for this page view */
  }
}

function fresh(visit, now) {
  return Boolean(visit && visit.at && now - Date.parse(visit.at) <= WINDOW_MS);
}

/** The referring site's host, or "" for internal navigation and direct visits. */
function externalReferrer() {
  try {
    if (!document.referrer) return "";
    const host = new URL(document.referrer).hostname.toLowerCase();
    return host === window.location.hostname.toLowerCase() ? "" : host;
  } catch {
    return "";
  }
}

export function captureVisit() {
  try {
    const now = Date.now();
    const params = new URLSearchParams(window.location.search);
    const visit = { at: new Date(now).toISOString(), path: window.location.pathname };
    CLICK_IDS.forEach((k) => {
      const v = params.get(k);
      if (v) visit[k] = v.slice(0, 300);
    });
    const utm = {};
    UTM.forEach((k) => {
      const v = params.get("utm_" + k);
      if (v) utm[k] = v.slice(0, 100);
    });
    if (Object.keys(utm).length) visit.utm = utm;
    const referrer = externalReferrer();
    if (referrer) visit.referrer = referrer;

    const saved = read();
    const isAd = CLICK_IDS.some((k) => visit[k]);
    const hasSource = isAd || Boolean(visit.utm || visit.referrer);
    write({
      // The first visit in the window: how they originally found the site.
      first: fresh(saved.first, now) ? saved.first : visit,
      // The most recent ad click in the window.
      ad: isAd ? visit : fresh(saved.ad, now) ? saved.ad : null,
      // The most recent visit that came from somewhere identifiable.
      last: hasSource ? visit : fresh(saved.last, now) ? saved.last : null,
    });
  } catch {
    /* never let tracking break the page */
  }
}

/** What the quote forms send as `attribution` (or null). */
export function getAttribution() {
  try {
    const now = Date.now();
    const s = read();
    const out = {
      first: fresh(s.first, now) ? s.first : null,
      ad: fresh(s.ad, now) ? s.ad : null,
      last: fresh(s.last, now) ? s.last : null,
    };
    return out.first || out.ad || out.last ? out : null;
  } catch {
    return null;
  }
}
